import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { MessageSquare, Plus, X, Send, Trash2, Edit3, Check, Phone, Mail, Users, User, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const CONVERSATION_TYPES = [
  { value: 'one_on_one', label: '1-on-1 Meeting', icon: User },
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'coaching', label: 'Coaching / Feedback', icon: MessageSquare },
  { value: 'disciplinary', label: 'Disciplinary', icon: MessageSquare },
  { value: 'check_in', label: 'Check-In', icon: Users },
  { value: 'other', label: 'Other', icon: MessageSquare }
];

function EmployeeConversations({ employee }) {
  const {
    addEmployeeConversation,
    updateEmployeeConversation,
    deleteEmployeeConversation,
    getEmployeeConversations
  } = useApp();

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'one_on_one',
    date: new Date().toISOString().split('T')[0],
    subject: '',
    notes: '',
    conductedBy: '',
    followUp: ''
  });

  const conversations = getEmployeeConversations(employee.id);

  const resetForm = () => {
    setFormData({
      type: 'one_on_one',
      date: new Date().toISOString().split('T')[0],
      subject: '',
      notes: '',
      conductedBy: '',
      followUp: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim()) {
      alert('Please enter a subject');
      return;
    }
    if (!formData.notes.trim()) {
      alert('Please enter conversation notes');
      return;
    }
    setSubmitting(true);

    try {
      if (editingId) {
        await updateEmployeeConversation(editingId, {
          type: formData.type,
          date: formData.date,
          subject: formData.subject,
          notes: formData.notes,
          conductedBy: formData.conductedBy,
          followUp: formData.followUp
        });
      } else {
        await addEmployeeConversation({
          employeeId: employee.id,
          type: formData.type,
          date: formData.date,
          subject: formData.subject,
          notes: formData.notes,
          conductedBy: formData.conductedBy,
          followUp: formData.followUp
        });
      }
      resetForm();
    } catch (err) {
      console.error('Error saving conversation:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (conv) => {
    setFormData({
      type: conv.type || 'one_on_one',
      date: conv.date || '',
      subject: conv.subject || '',
      notes: conv.notes || '',
      conductedBy: conv.conductedBy || '',
      followUp: conv.followUp || ''
    });
    setEditingId(conv.id);
    setShowForm(true);
  };

  const handleDelete = async (conv) => {
    if (!window.confirm(`Delete this conversation record? This cannot be undone.`)) return;
    try {
      await deleteEmployeeConversation(conv.id);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete.');
    }
  };

  const getTypeConfig = (type) => {
    return CONVERSATION_TYPES.find(t => t.value === type) || CONVERSATION_TYPES[CONVERSATION_TYPES.length - 1];
  };

  return (
    <div>
      <div className="timeoff-header">
        <h3><MessageSquare size={18} /> Conversations</h3>
        <button
          className={`btn ${showForm ? 'btn-secondary' : 'btn-primary'} btn-sm`}
          onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
        >
          {showForm ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Log Conversation</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="manager-timeoff-form">
          <div className="mtf-row">
            <div className="mtf-group">
              <label>Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                {CONVERSATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="mtf-group">
              <label>Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="mtf-group mtf-grow">
              <label>Conducted By</label>
              <input
                type="text"
                value={formData.conductedBy}
                onChange={(e) => setFormData({ ...formData, conductedBy: e.target.value })}
                placeholder="e.g. Sarah, Manager"
              />
            </div>
          </div>
          <div className="mtf-row">
            <div className="mtf-group mtf-grow">
              <label>Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Brief description of conversation topic"
              />
            </div>
          </div>
          <div className="mtf-row">
            <div className="mtf-group mtf-grow">
              <label>Notes *</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Key points discussed, decisions made, action items..."
                rows={4}
                style={{ resize: 'vertical', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit' }}
              />
            </div>
          </div>
          <div className="mtf-row">
            <div className="mtf-group mtf-grow">
              <label>Follow-Up</label>
              <input
                type="text"
                value={formData.followUp}
                onChange={(e) => setFormData({ ...formData, followUp: e.target.value })}
                placeholder="Any follow-up actions or next steps"
              />
            </div>
          </div>
          <div className="mtf-footer">
            <div />
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              {submitting ? 'Saving...' : <><Send size={14} /> {editingId ? 'Update' : 'Save'}</>}
            </button>
          </div>
        </form>
      )}

      {conversations.length === 0 && !showForm ? (
        <div className="empty-timeoff">No conversations documented</div>
      ) : (
        <div className="emp-conv-list">
          {conversations.map(conv => {
            const typeConfig = getTypeConfig(conv.type);
            const TypeIcon = typeConfig.icon;
            const isExpanded = expandedId === conv.id;

            return (
              <div key={conv.id} className={`emp-conv-row ${conv.type === 'disciplinary' ? 'disciplinary' : ''}`}>
                <div
                  className="emp-conv-row-header"
                  onClick={() => setExpandedId(isExpanded ? null : conv.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="emp-conv-row-left">
                    <span className={`emp-conv-type-icon ${conv.type}`}>
                      <TypeIcon size={14} />
                    </span>
                    <div className="emp-conv-info">
                      <span className="emp-conv-subject">{conv.subject}</span>
                      <span className="emp-conv-meta">
                        {typeConfig.label}
                        {conv.date && <> &middot; {format(parseISO(conv.date), 'MMM d, yyyy')}</>}
                        {conv.conductedBy && <> &middot; {conv.conductedBy}</>}
                      </span>
                    </div>
                  </div>
                  <div className="emp-conv-row-right">
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="emp-conv-details">
                    <div className="emp-conv-notes">
                      <strong>Notes:</strong>
                      <p>{conv.notes}</p>
                    </div>
                    {conv.followUp && (
                      <div className="emp-conv-followup">
                        <strong>Follow-Up:</strong> {conv.followUp}
                      </div>
                    )}
                    <div className="emp-conv-detail-actions">
                      <button
                        className="emp-doc-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleEdit(conv); }}
                        title="Edit"
                      >
                        <Edit3 size={14} /> Edit
                      </button>
                      <button
                        className="emp-doc-action-btn emp-doc-delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(conv); }}
                        title="Delete"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default EmployeeConversations;
